import React, { useEffect, useState } from "react";
import InputLabel from "./InputLabel";
import ErrorMessage from "../ErrorMessages/ErrorMessage";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import TertiaryButton from "../buttons/TertiaryButton";
import Image from "../images/Image";
import InputShimmer from "./InputShimmer";
import OTPVerification from "../Verifications/OTPVerification";
import { breadcrumbsContent } from "../breadcrumbs/BreadcrumbsContent";
import useTranslation from "../CustomHooks/useTranslation";
import { inputFieldContent } from "./InputFieldContent";

interface InputItem {
  labelName: string;
  id: string;
}

interface InputFieldForPhoneNumberProps {
  inputItem: InputItem;
  errorMessage?: string;
  onChange: (id: string, value: string) => void;
  required: boolean;
  handleVerify?: (id: string, resend: boolean) => void;
  fieldError?: string | boolean;
  handleSubmitOTP: () => void;
  disabled?: boolean;
  isVerified: string;
  ButtonLoading?: boolean;
  value?: string;
  editLoading?: boolean;
  customClass?: string;
  verification?: boolean;
  isOpenOtpModel?: boolean;
  setIsOpenOtpModel?: React.Dispatch<React.SetStateAction<boolean>>;
  countryCode?: string;
  mapping?:boolean;
  maxLength?: number;
}

export default function InputFieldForPhoneNumber({
  inputItem: { labelName, id },
  errorMessage,
  required,
  onChange,
  fieldError,
  isVerified,
  editLoading,
  handleVerify,
  value,
  ButtonLoading,
  disabled,
  customClass,
  isOpenOtpModel,
  handleSubmitOTP,
  setIsOpenOtpModel,
  verification = true,
  countryCode,
  maxLength,
  mapping
}: InputFieldForPhoneNumberProps) {
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("af");
  const { translate, notEnglish } = useTranslation(breadcrumbsContent);
  const { replace } = useTranslation(inputFieldContent);
  const [key, setKey] = useState(0); // Add a key to force re-render

  // Reset the country and force re-render when page changes
  useEffect(() => {
    setSelectedCountry("af");
    setKey(prevKey => prevKey + 1); // Increment key to force re-render
  }, [window.location.href]);

  const handleClickVerify = () => {
    setIsModelOpen(true);
    if (handleVerify) {
      handleVerify(id, false);
    }
  };

  const handleChange = (value: string, phoneInfo: { [key: string]: string }) => {
    if (maxLength && value.length > maxLength) {
      return;
    }
    onChange('country_code', phoneInfo?.dialCode);
    onChange(id, value.trim());
  };

  useEffect(() => {
    if (!isOpenOtpModel) {
      setIsModelOpen(false);
    }
  }, [isOpenOtpModel]);

  return (
    <div className="w-full">
      <InputLabel labelName={labelName} id={id} required={required} />

      {editLoading ? (
        <InputShimmer className={'mapping'}/>
      ) : (
        <div className="relative flex items-center" data-testid={id}>
          <PhoneInput
            key={key} // Add key to force re-render on page change
            country={selectedCountry}
            disabled={disabled}
            value={value}
            onChange={(phone, phoneInfo) => handleChange(phone, phoneInfo || {})}
            inputClass={`${disabled
              ? customClass ? 'custom-phone-number-disabled-class' : "custom-phone-disabled-class"
              : fieldError || errorMessage
                ? customClass
                  ? "custom-number-error-class"
                  : "custom-phone-error-class"
                : customClass
                  ? "custom-phone-number-class"
                  : "custom-phone-class"
              }  ${isVerified ? 'pr-[45px]' : 'pr-[70px]'} rounded-lg`}
            enableSearch={true}
            disableSearchIcon={true}
            dropdownClass={disabled ? "" : "custom-dropdown"}
            buttonClass={`${disabled
              ? `flag-dropdown-disabled ${notEnglish ? 'react-selected-flag' : ''}`
              : fieldError || errorMessage
                ? "flag-dropdown-error"
                : `flag-dropdown-active ${notEnglish ? 'react-selected-flag' : ''}`
              }`}
            searchPlaceholder=""
            onBlur={(e, data) => {
              if (e.target.classList.contains('country-list')) {
                const searchInput = document.querySelector('.search-box') as HTMLInputElement;
                if (searchInput) {
                  searchInput.value = '';
                }
              }
            }}
          />

          {verification && (
            <div
              className={`absolute right-0  mr-2.5 ${ButtonLoading || isVerified ? "top-[18px]" : "top-2"
                }`}
            >
              {value &&
                value.length >= 7 &&
                (isVerified ? (
                  <Image
                    src="success-tick"
                    className="h-5 w-5 right-0 top-[18px] mr-2.5"
                  />
                ) : !disabled || ButtonLoading ? (
                  <TertiaryButton
                    testId={`${id}_verify`}
                    buttonName={translate("Verify")}
                    id="Verify"
                    handleClick={handleClickVerify}
                    loading={ButtonLoading}
                  />
                ) : null)}
            </div>
          )}
        </div>
      )}

      {(isModelOpen && isOpenOtpModel) && (
        <OTPVerification
          handleSubmitOTP={handleSubmitOTP}
          handleClickResendOTP={handleVerify || (() => { })}
          instructionText={replace(`We have sent a code to {{email_or_phone}}`, {
            email_or_phone: `${countryCode ? `+${countryCode} ${value && value.trim().replace(countryCode, '') || ''}` : ''}`,
          })}
          handleClose={() => { setIsOpenOtpModel?.(false); setIsModelOpen(false) }}
          otp_id={id}
        />
      )}

      {(errorMessage && !disabled) && <ErrorMessage errorMessage={errorMessage} />}
    </div>
  );
}